import React, { useState, useEffect } from "react";
import "./cart.css";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Nav from "../components/Nav";
import { isLoggedIn as checkLogin } from "../utils/session";
import API_BASE from "../api/api";
import toast from "react-hot-toast";

// 🔥 Assets folder se cart icon import kiya hai
import cartIcon from "../assets/cart-icon.png";

const Cart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartItems, setCartItems] = useState([]);

  /* ================= LOGIN CHECK ================= */
  useEffect(() => {
    if (!checkLogin()) {
      navigate("/login", {
        state: { from: location.pathname },
        replace: true,
      });
    }
  }, [navigate, location.pathname]);

  /* ================= FETCH CART ================= */
  const fetchCart = async () => {
    try {
      const res = await fetch(`${API_BASE}/getcart.php`, {
        method: "GET",
        credentials: "include",
      });

      const data = await res.json();
      console.log("CART DATA:", data);

      if (data.status === "success") {
        setCartItems(data.data);
      } else {
        setCartItems([]);
      }
    } catch (err) {
      console.error("Error fetching cart:", err);
    }
  };

  useEffect(() => {
    fetchCart();

    const handleCartUpdate = () => fetchCart();
    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, []);

  /* ================= UPDATE QUANTITY ================= */
  const updateQuantity = async (product_id, delta) => {
    const item = cartItems.find((i) => i.product_id === product_id);
    if (!item) return;

    const newQty = item.quantity + delta;

    try {
      const res = await fetch(`${API_BASE}/updatecart.php`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id, quantity: newQty }),
      });

      const data = await res.json();

      if (data.status !== "success") throw new Error(data.message);

      if (newQty > 0) {
        setCartItems((prev) =>
          prev.map((i) =>
            i.product_id === product_id ? { ...i, quantity: newQty } : i
          )
        );
      } else {
        setCartItems((prev) =>
          prev.filter((i) => i.product_id !== product_id)
        );
      }

      window.dispatchEvent(new Event("cartUpdated"));
    } catch (err) {
      console.error("Error updating cart:", err);
    }
  };

  /* ================= CLEAR CART (DB) ================= */
  const clearCartFromDB = async () => {
    try {
      const res = await fetch(`${API_BASE}/clearcart.php`, {
        method: "POST",
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        toast.success("Cart cleared successfully");
        setCartItems([]);
        window.dispatchEvent(new Event("cartUpdated"));
      } else {
        toast.error("Failed to clear cart");
      }
    } catch (err) {
      console.error("Clear cart error:", err);
    }
  };

  const totalPrice = cartItems.reduce((total, item) => {
    return total + (parseFloat(item.price) || 0) * item.quantity;
  }, 0);

  /* ================= CHECKOUT ================= */
  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/getadd.php`, {
        method: "GET",
        credentials: "include",
      });

      const data = await response.json();

      if (Array.isArray(data) && data.length > 0) {
        navigate("/pay", { state: { addressId: data[0].id } });
      } else {
        navigate("/addaddress");
      }
    } catch (error) {
      console.error("Error checking address:", error);
      navigate("/addaddress");
    }
  };

  return (
    <>
      <Nav />
      <Navbar />

      <div className="ssp-checkout-bg">
        <div className="checkout-page">
          <div className="cart-section">
            
            {/* Title me emoji hata kar image asset laga diya hai */}
            <h2 className="section-title">
              <img src={cartIcon} alt="Cart" className="cart-title-icon-img" /> 
              Shopping Cart
            </h2>

            {cartItems.length > 0 ? (
              <>
                <table className="cart-table">
                  <thead>
                    <tr>
                      <th>Book</th>
                      <th>Quantity</th>
                      <th>Total Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartItems.map((item) => (
                      <tr key={item.product_id}>
                        <td>
                          <div className="product-info">
                            <Link to={`/book/${item.product_id}`}>
                              <img
                                src={item.image || "/no-image.png"}
                                alt={item.name}
                                style={{ cursor: "pointer" }}
                              />
                            </Link>

                            <Link
                              to={`/book/${item.product_id}`}
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                                cursor: "pointer",
                              }}
                            >
                              <span>{item.name}</span>
                            </Link>
                          </div>
                        </td>
                        <td>
                          <div className="quantity-control">
                            <button
                              onClick={() =>
                                updateQuantity(item.product_id, -1)
                              }
                            >
                              -
                            </button>
                            <span>{item.quantity}</span>
                            <button
                              onClick={() =>
                                updateQuantity(item.product_id, 1)
                              }
                            >
                              +
                            </button>
                          </div>
                        </td>
                        <td>
                          ₹
                          {(parseFloat(item.price) * item.quantity).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="cart-summary">
                  <p>
                    Total Amount:{" "}
                    <strong>₹{totalPrice.toFixed(2)}</strong>
                  </p>

                  <div style={{ display: "flex", gap: "12px" }}>
                    <Link to="/book" className="continue-shopping">
                      ← Continue Shopping
                    </Link>

                    <button
                      className="clear-cart-btn"
                      onClick={clearCartFromDB}
                    >
                      Clear Cart
                    </button>
                  </div>
                </div>

                <button className="place-btn" onClick={handleCheckout}>
                  Proceed to Checkout
                </button>
              </>
            ) : (
              <p className="empty-cart">
                Your cart is empty. <Link to="/book">Go Shopping →</Link>
              </p>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Cart;